import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { num } from "@/lib/format";
import { SimArticle, blankArticle } from "@/components/planning/SimArticle";
import { MaterialsCheck } from "@/components/planning/MaterialsCheck";

const payload = (s) => ({
  name: s.name, season: s.season, notes: s.notes || "",
  articles: s.articles.map((a) => ({
    ...a, colors: a.colors.map((c) => ({ color: c.color, quantity: Number(c.quantity) || 0, sizes: Object.fromEntries(Object.entries(c.sizes || {}).map(([k, v]) => [k, Number(v) || 0])) })),
    fabrics: a.fabrics.map((f) => ({ ...f, meters_per_piece: f.meters_per_piece === "" ? null : Number(f.meters_per_piece) })),
  })),
});

export const SimEditor = ({ sim, sheets, onClose }) => {
  const qc = useQueryClient();
  const [s, setS] = useState(() => ({ articles: [blankArticle()], ...sim }));
  const body = payload(s);
  const key = JSON.stringify(body.articles);
  const [debounced, setDebounced] = useState(key);
  useEffect(() => { const h = setTimeout(() => setDebounced(key), 350); return () => clearTimeout(h); }, [key]);
  const { data: opts = {} } = useQuery({ queryKey: ["model-sheet-options"], queryFn: async () => (await api.get("/model-sheets/options")).data });
  const { data: res, isFetching } = useQuery({ queryKey: ["sim-preview", debounced], queryFn: async () => (await api.post("/simulations/preview", { ...body, articles: JSON.parse(debounced) })).data, placeholderData: (p) => p });
  const done = () => qc.invalidateQueries({ queryKey: ["simulations"] });
  const save = useMutation({
    mutationFn: async () => (await (s.id ? api.put(`/simulations/${s.id}`, body) : api.post("/simulations", body))).data,
    onSuccess: (d) => { done(); setS({ ...s, id: d.id }); toast.success("Simulazione salvata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Salvataggio non riuscito"),
  });
  const del = useMutation({ mutationFn: async () => api.delete(`/simulations/${s.id}`), onSuccess: () => { done(); onClose(); } });
  const toOrder = (res?.fabrics || []).concat(res?.accessories || []).filter((r) => r.to_order > 0).length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto [&>*]:min-w-0" data-testid="sim-editor">
        <DialogHeader><DialogTitle>Pianificazione produzione <span className="ml-2 text-sm font-normal text-muted-foreground">{s.season} · non scala nulla dal magazzino</span></DialogTitle></DialogHeader>
        <datalist id="sim-fabric-types">{(opts.fabric_types || []).map((x) => <option key={x} value={x} />)}</datalist>
        <datalist id="sim-colors">{(opts.colors || []).map((x) => <option key={x} value={x} />)}</datalist>
        <datalist id="sim-accessories">{(opts.accessories || []).map((x) => <option key={x} value={x} />)}</datalist>
        <Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder="Nome simulazione (es. Collezione A26 prima stima)" data-testid="sim-name" />
        <div className="space-y-3">
          {s.articles.map((a, i) => <SimArticle key={i} a={a} i={i} sheets={sheets} onChange={(x) => setS({ ...s, articles: s.articles.map((y, j) => (j === i ? x : y)) })} onRemove={() => setS({ ...s, articles: s.articles.filter((_, j) => j !== i) })} />)}
          <Button variant="outline" onClick={() => setS({ ...s, articles: [...s.articles, blankArticle()] })} data-testid="sim-add-article"><Plus className="mr-1 h-4 w-4" />Aggiungi articolo</Button>
        </div>
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <p className="mb-3 flex items-center gap-2 font-display text-sm font-semibold" data-testid="sim-summary">
            Risultato: {num(res?.pieces || 0)} capi · {toOrder ? <span className="text-rose-400">{toOrder} {toOrder === 1 ? "materiale" : "materiali"} da ordinare</span> : <span className="text-emerald-400">materiale sufficiente</span>}
            {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          </p>
          <MaterialsCheck res={res} />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div>{s.id && <Button variant="ghost" className="text-rose-400" onClick={() => window.confirm("Eliminare la simulazione?") && del.mutate()} data-testid="sim-delete-btn"><Trash2 className="mr-1 h-4 w-4" />Elimina</Button>}</div>
          <Button onClick={() => (s.name.trim() ? save.mutate() : toast.error("Dai un nome alla simulazione"))} disabled={save.isPending} data-testid="sim-save-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva simulazione</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
